import { useState } from 'react';
import { Link } from 'react-router-dom';
import SchedulePicker, { buildScheduleDateOptions } from '@/components/booking/SchedulePicker';
import Avatar from '@/components/base/Avatar';

interface BookingItem {
  id: string;
  bookingId: string;
  mentorId: string;
  mentorName: string;
  mentorAvatar: string;
  mentorTitle: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  preferTime: string;
  consultGoal: string;
  consultPrice: number;
  consultDuration: string;
  bookingDate: string;
  bookingTime: string;
  bookingTimezone: string;
  date: string;
  payTime: string;
  serialNumber: string;
  remark: string;
  status: 'paid' | 'pending' | 'closed';
  bookingStatus: number;
  bookingStatusText: string;
}

const PAY_STATUS_TEXT = {
  paid: '已付款',
  pending: '待付款',
  closed: '已关闭'
};

const PAY_STATUS_CLASS = {
  paid: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  pending: 'bg-amber-50 text-amber-600 border-amber-100',
  closed: 'bg-slate-100 text-slate-500 border-slate-200'
};

const MyBookings = ({
  bookings,
  onCancel,
  onContinuePay,
  onSetSchedule
}: {
  bookings: BookingItem[];
  onCancel: (id: string) => void;
  onContinuePay: (bookingId: string) => void;
  onSetSchedule: (payload: { id: string; bookingDate: string; bookingTime: string }) => Promise<void>;
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [bookingDateInput, setBookingDateInput] = useState('');
  const [bookingTimeInput, setBookingTimeInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const dateOptions = buildScheduleDateOptions(21);

  const startEdit = (booking: BookingItem) => {
    setEditingId(booking.id);
    setBookingDateInput(booking.bookingDate || '');
    setBookingTimeInput(booking.bookingTime || '');
    setScheduleError('');
  };

  const saveSchedule = async () => {
    if (!editingId || !bookingDateInput || !bookingTimeInput) {
      setScheduleError('请先选择完整的预约日期和时间');
      return;
    }
    setSaving(true);
    setScheduleError('');
    try {
      await onSetSchedule({ id: editingId, bookingDate: bookingDateInput, bookingTime: bookingTimeInput });
      setEditingId(null);
    } catch (error: unknown) {
      setScheduleError(error instanceof Error ? error.message : '设置时间失败，请稍后再试');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
      {bookings.length === 0 ? (
        <div className="py-16 text-center text-slate-400 text-sm">暫無預約記錄</div>
      ) : (
        <div className="divide-y divide-slate-50">
          {bookings.map((booking) => (
            <div key={booking.id} className="px-6 py-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex items-start gap-4">
                  <Avatar src={booking.mentorAvatar} alt={booking.mentorName} className="w-14 h-14 rounded-2xl flex-shrink-0" />
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-[#0A1628]">{booking.mentorName}</p>
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${PAY_STATUS_CLASS[booking.status]}`}>
                        {PAY_STATUS_TEXT[booking.status]}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-600">
                        {booking.bookingStatusText}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{booking.mentorTitle || '1V1 導師諮詢'}</p>
                    <p className="text-[11px] text-slate-400 mt-1 font-mono">{booking.bookingId}</p>
                  </div>
                </div>

                <div className="text-left lg:text-right">
                  <p className="text-lg font-black text-[#0A1628]">${booking.consultPrice}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{booking.consultDuration}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{booking.date || '-'}</p>
                </div>
              </div>

              <div className="mt-4 grid gap-3 rounded-2xl bg-slate-50 px-4 py-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <p className="text-[11px] font-semibold text-slate-400">聯絡人</p>
                  <p className="text-sm text-[#0A1628] mt-1">{booking.contactName || '-'}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-slate-400">信箱</p>
                  <p className="text-sm text-[#0A1628] mt-1 break-all">{booking.contactEmail || '-'}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-slate-400">偏好時段</p>
                  <p className="text-sm text-[#0A1628] mt-1">{booking.preferTime || '-'}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-slate-400">支付流水</p>
                  <p className="text-sm text-[#0A1628] mt-1 font-mono">{booking.serialNumber || '-'}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-slate-400">已设预约时间</p>
                  <p className="text-sm text-[#0A1628] mt-1">{booking.bookingDate && booking.bookingTime ? `${booking.bookingDate} ${booking.bookingTime}` : '-'}</p>
                </div>
              </div>

              {booking.consultGoal ? (
                <div className="mt-3">
                  <p className="text-[11px] font-semibold text-slate-400">諮詢目標</p>
                  <p className="text-sm text-slate-600 leading-relaxed mt-1">{booking.consultGoal}</p>
                </div>
              ) : null}

              <div className="mt-4 flex flex-wrap items-center gap-3">
                {booking.status === 'pending' && booking.bookingStatus !== 4 ? (
                  <button
                    onClick={() => onContinuePay(booking.id)}
                    className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                  >
                    繼續付款
                  </button>
                ) : null}
                {booking.status === 'pending' && booking.bookingStatus !== 4 ? (
                  <button
                    onClick={() => onCancel(booking.id)}
                    className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-700"
                  >
                    取消預約
                  </button>
                ) : null}
                {booking.status === 'paid' && ![4, 5].includes(booking.bookingStatus) ? (
                  <button
                    onClick={() => startEdit(booking)}
                    className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-700 transition-colors hover:bg-violet-100"
                  >
                    {booking.bookingDate && booking.bookingTime ? '修改预约时间' : '设置预约时间'}
                  </button>
                ) : null}
                <Link
                  to={`/mentor/${booking.mentorId}`}
                  className="rounded-xl bg-[#0A1628] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#12233f]"
                >
                  查看導師
                </Link>
              </div>

              {editingId === booking.id ? (
                <div className="mt-4 rounded-2xl border border-violet-200 bg-violet-50/60 p-4">
                  <p className="text-sm font-semibold text-violet-800">设置本次预约时间</p>
                  <div className="mt-3">
                    <SchedulePicker
                      selectedDate={bookingDateInput}
                      selectedTime={bookingTimeInput}
                      onDateChange={setBookingDateInput}
                      onTimeChange={setBookingTimeInput}
                      dateOptions={dateOptions}
                    />
                  </div>
                  {scheduleError ? <p className="mt-2 text-xs text-rose-500">{scheduleError}</p> : null}
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      onClick={saveSchedule}
                      disabled={saving}
                      className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-700 disabled:opacity-60"
                    >
                      {saving ? '保存中...' : '保存时间'}
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      disabled={saving}
                      className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-700"
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyBookings;
